import React from 'react';

import { Tooltip } from '@components';
import formatCurrency from '@utils/numberFormater';
import { capitalizeFirstLetter } from '@utils/stringFormat';
import { LeadAnalyticsLocation } from 'types/lead-management';

import AnalyticsCardState from './AnalyticsCardState';
import AnalyticsViewSelector from './AnalyticsViewSelector';

import {
  CONVERSION_TICKS,
  LeadAnalyticsCriteria
} from '../../constants/leadAnalytics';
import { getBarPercent, getDistributionTicks } from '../../helper';
import { LOCATION_BAR_COLOR } from '../../utils/analyticsColors';

interface TopLocationsCardProps {
  locations: LeadAnalyticsLocation[];
  view: LeadAnalyticsCriteria;
  onViewChange: (view: LeadAnalyticsCriteria) => void;
  isLoading?: boolean;
  className?: string;
}

const TopLocationsCard: React.FC<TopLocationsCardProps> = ({
  locations,
  view,
  onViewChange,
  isLoading = false,
  className
}) => {
  const isConversion = view === LeadAnalyticsCriteria.CONVERSION;
  const totalCount = locations?.reduce((acc, curr) => acc + curr.count, 0) ?? 0;

  const tickValues = isConversion
    ? CONVERSION_TICKS
    : getDistributionTicks(totalCount);

  return (
    <div
      className={`flex min-h-90 w-full min-w-70 flex-1 flex-col justify-between rounded-xl border border-grey10 bg-white p-6 shadow-md ${
        className || ''
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="text-20 font-semibold text-content">
          Leads By Top Locations
        </div>
        <AnalyticsViewSelector
          dropdownId="top-locations-view"
          view={view}
          onViewChange={onViewChange}
        />
      </div>
      <div className="flex flex-1 flex-col justify-end gap-4">
        <AnalyticsCardState
          isLoading={isLoading}
          hasData={totalCount > 0}
          emptyCardType="location"
        >
          <div className="mt-6 flex flex-col gap-4">
            {locations.map((loc) => {
              const perc = getBarPercent(
                loc.count,
                totalCount,
                isConversion,
                loc.percentage
              );
              return (
                <div key={loc.location} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-14 min-w-0 flex-1 break-words text-darkGrey">
                      {capitalizeFirstLetter(loc.location)}
                    </span>
                    <span className="text-14 invisible shrink-0 text-darkGrey">
                      {loc.count}
                    </span>
                  </div>
                  <div className="h-4 w-full rounded bg-cofeeGreyBlue100">
                    <div
                      className="h-4 rounded"
                      style={{
                        width: `${perc}%`,
                        backgroundColor: LOCATION_BAR_COLOR
                      }}
                      data-tooltip-id={`${loc.location}-bar-chart`}
                    />
                  </div>
                  <Tooltip
                    id={`${loc.location}-bar-chart`}
                    className="!rounded-lg !bg-cofeeNeutral11 !opacity-100"
                  >
                    <div className="text-14 whitespace-nowrap text-white">
                      Revenue: <span className="mr-0.5 font-inter">₹</span>
                      {formatCurrency((loc.revenue ?? 0) / 100, 'INR')} •{' '}
                      {loc.count} Leads
                    </div>
                  </Tooltip>
                </div>
              );
            })}
            {/* Range axis */}
            <div>
              <div className="text-12 mt-2 flex w-full justify-between text-cofeeGreyBlue500">
                {tickValues.map((label) => (
                  <span key={label}>
                    {isConversion ? `${label}%` : label.toLocaleString()}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </AnalyticsCardState>
      </div>
    </div>
  );
};

export default TopLocationsCard;
